import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Send,
  X,
  ShieldCheck,
  Store,
  Sparkles,
  Car,
  Loader2,
  Lock,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/providers/auth-provider'
import { appRoutes } from '@/constants'
import {
  useStartConversation,
  useMessages,
  useSendMessage,
  useMarkAsRead,
} from '../hooks/use-chat'
import type { Conversation } from '../types/chat.types'

interface CarChatDrawerProps {
  isOpen: boolean
  onClose: () => void
  car: {
    id: number
    title: string
    slug: string
    askingPrice: number | string
    images?: any
    user?: {
      id: number
      firstName?: string
      lastName?: string
      role?: string
    }
  }
  seller: {
    id?: number
    name: string
    type?: string
    phone?: string
    email?: string
    verified?: boolean
  }
}

const QUICK_PROMPTS = [
  'Is this vehicle still available?',
  'What is your best negotiated price?',
  'Can I schedule an inspection or test drive?',
  'Can you share the full service history?',
]

export function CarChatDrawer({ isOpen, onClose, car, seller }: CarChatDrawerProps) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null)
  const [messageText, setMessageText] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const startConversation = useStartConversation()
  const sendMessage = useSendMessage()
  const markAsRead = useMarkAsRead()

  const conversationId = activeConversation?.id ?? null
  const { data: messages = [], isLoading: isLoadingMessages } = useMessages(conversationId)

  // Determine sellerId
  const sellerId = seller.id || car.user?.id

  // When drawer opens, find or start conversation
  useEffect(() => {
    if (isOpen && user && sellerId && sellerId !== user.id) {
      startConversation.mutate(
        {
          sellerId,
          carListingId: car.id,
        },
        {
          onSuccess: (res) => {
            if (res.data) {
              setActiveConversation(res.data)
              markAsRead.mutate(res.data.id)
            }
          },
        },
      )
    }
  }, [isOpen, user, sellerId, car.id])

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  if (!isOpen) return null

  // Extract first image
  let carImage = 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=300&q=80'
  if (Array.isArray(car.images) && car.images.length > 0) {
    const first = car.images[0]
    carImage = typeof first === 'string' ? first : first.url || carImage
  }

  const handleSend = async (contentToSend?: string) => {
    const text = (contentToSend || messageText).trim()
    if (!text || !activeConversation) return

    sendMessage.mutate(
      {
        conversationId: activeConversation.id,
        content: text,
      },
      {
        onSuccess: () => {
          if (!contentToSend) setMessageText('')
        },
      },
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleLoginRedirect = () => {
    navigate(appRoutes.auth.login, { state: { from: location.pathname } })
  }

  const isOwnListing = user && sellerId === user.id

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[95vw] sm:w-[440px] max-h-[88vh] h-[640px] flex flex-col rounded-3xl bg-card/95 backdrop-blur-2xl border border-white/20 dark:border-white/10 shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-6 duration-300">
      {/* Header */}
      <div className="p-4 bg-background/80 border-b border-border/80 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative">
            <div className="size-11 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center font-bold text-primary font-display text-sm">
              {seller.name.slice(0, 2).toUpperCase()}
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 size-3 bg-emerald-500 rounded-full border-2 border-background ring-1 ring-emerald-500/20" />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h4 className="font-display text-sm font-bold text-foreground truncate">
                {seller.name}
              </h4>
              {seller.verified && (
                <ShieldCheck className="size-4 text-emerald-500 shrink-0" />
              )}
            </div>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Store className="size-3" /> {seller.type || 'Verified Dealer'} • Typically replies within 1 hr
            </p>
          </div>
        </div>

        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          className="size-8 p-0 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </Button>
      </div>

      {/* Car Reference Card Header */}
      <div className="px-4 py-2.5 bg-muted/40 border-b border-border/60 flex items-center gap-3">
        <img
          src={carImage}
          alt={car.title}
          className="size-12 rounded-xl object-cover border border-border/80 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-foreground truncate">{car.title}</p>
          <p className="text-xs font-extrabold text-primary font-display">
            ₹{Number(car.askingPrice).toLocaleString('en-IN')}
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] shrink-0 border-primary/30 text-primary">
          Live Vehicle
        </Badge>
      </div>

      {/* Body: Not Logged In, Own Listing, or Chat Thread */}
      {!user ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center space-y-4">
          <div className="size-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
            <Lock className="size-7" />
          </div>
          <div>
            <h3 className="font-display font-bold text-base text-foreground">Sign In to Chat</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-xs">
              Connect directly with verified dealers and private sellers to discuss pricing, test drives, and inspections.
            </p>
          </div>
          <Button
            onClick={handleLoginRedirect}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold glow-md"
          >
            Sign In &amp; Message Seller
          </Button>
        </div>
      ) : isOwnListing ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center space-y-3">
          <Car className="size-10 text-muted-foreground" />
          <h4 className="font-bold text-sm text-foreground">This is your listing</h4>
          <p className="text-xs text-muted-foreground">
            You cannot message yourself. Inquiries from customers will appear in your Seller/Dealer Dashboard.
          </p>
        </div>
      ) : (
        <>
          {/* Messages Thread */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3.5">
            {isLoadingMessages || startConversation.isPending ? (
              <div className="h-full flex flex-col items-center justify-center text-muted-foreground space-y-2">
                <Loader2 className="size-6 animate-spin text-primary" />
                <p className="text-xs">Connecting with dealer...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="space-y-4 pt-2">
                <div className="text-center p-4 rounded-2xl bg-primary/5 border border-primary/15">
                  <Sparkles className="size-5 text-primary mx-auto mb-1.5" />
                  <p className="text-xs font-bold text-foreground">Start conversation</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Ask a question about {car.title} or select a quick question below.
                  </p>
                </div>

                {/* Quick Prompts */}
                <div className="space-y-1.5">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                    Quick Inquiries:
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {QUICK_PROMPTS.map((prompt, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleSend(prompt)}
                        className="text-left text-xs p-2.5 rounded-xl bg-card hover:bg-accent border border-border text-foreground transition-all duration-150 flex items-center justify-between group"
                      >
                        <span>{prompt}</span>
                        <Send className="size-3 text-muted-foreground group-hover:text-primary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              messages.map((msg) => {
                const isMe = msg.senderId === user.id
                return (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
                  >
                    <div
                      className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-xs leading-relaxed ${
                        isMe
                          ? 'bg-primary text-primary-foreground rounded-br-xs font-medium shadow-sm'
                          : 'bg-muted/80 text-foreground border border-border/60 rounded-bl-xs'
                      }`}
                    >
                      {msg.content}
                    </div>
                    <span className="text-[10px] text-muted-foreground mt-1 px-1">
                      {new Date(msg.createdAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                )
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Bottom Send Input */}
          <div className="p-3 bg-background/90 border-t border-border/80 space-y-2">
            <div className="relative flex items-end gap-2 bg-muted/60 border border-border rounded-2xl p-2 focus-within:border-primary/60 focus-within:ring-1 focus-within:ring-primary/20 transition-all">
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message to seller..."
                rows={1}
                className="w-full max-h-24 resize-none bg-transparent border-0 p-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
              <Button
                type="button"
                size="sm"
                onClick={() => handleSend()}
                disabled={!messageText.trim() || sendMessage.isPending}
                className="size-8 p-0 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shrink-0 glow-sm"
              >
                {sendMessage.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Send className="size-3.5" />
                )}
              </Button>
            </div>
            <p className="text-[10px] text-center text-muted-foreground">
              Press <kbd className="px-1 py-0.5 rounded bg-muted text-[9px] font-mono">Enter</kbd> to send • Direct secure chat
            </p>
          </div>
        </>
      )}
    </div>
  )
}
